import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_POST } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

export interface ShiftRequestPayload {
    skills: string[];
    team: string | null;
    postDate: string;
    postById: string;
    postByName: string;
    requestDate: string;
    startTime: string;
    endTime: string;
    requestType: string;
    requestSite: string;
    requestShiftId: string;
    requestEmplCount: string;
    requestNotes: string;
    requestNotifyDate: string | null;
    requestNotifyTime: string | null;
    scheduleNotificationFl: boolean;
    eventAction?: string[];
}

interface CreateShiftState {
    shiftRequest: ShiftRequestPayload | null;
    loading: boolean;
    error: string | null;
}

const initialState: CreateShiftState = {
    shiftRequest: null,
    loading: false,
    error: null,
};

export const createShiftRequest = createAsyncThunk(
    'ShiftRequest/createShiftRequest',
    async ({ payload }: { payload: ShiftRequestPayload }, { rejectWithValue }) => {
        try {
            // Transform camelCase to PascalCase for C# backend
            // Send directly without wrapping in 'request' property
            const requestBody = {
                Skills: payload.skills,
                Team: payload.team,
                PostDate: payload.postDate,
                PostById: payload.postById,
                PostByName: payload.postByName,
                RequestDate: payload.requestDate,
                StartTime: payload.startTime,
                EndTime: payload.endTime,
                RequestType: payload.requestType,
                RequestSite: payload.requestSite,
                RequestShiftId: payload.requestShiftId,
                RequestEmplCount: payload.requestEmplCount,
                RequestNotes: payload.requestNotes,
                RequestNotifyDate: payload.requestNotifyDate,
                RequestNotifyTime: payload.requestNotifyTime,
                ScheduleNotificationFl: payload.scheduleNotificationFl,
                EventAction: payload.eventAction,
            };

            const response = await API_POST(`${ENDPOINTS.CREATE_SHIFT_REQUEST}`, requestBody);
            return Array.isArray(response) ? response : [response];
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to create shift');
        }
    }
);

const createShiftRequestSlice = createSlice({
    name: 'createShiftRequest',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createShiftRequest.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createShiftRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.shiftRequest = action.meta.arg.payload;
            })
            .addCase(createShiftRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = createShiftRequestSlice.actions;
export default createShiftRequestSlice.reducer;